import { z } from "zod";
import {
  AUTH_MODES,
  INVOICE_STATUSES,
  MATTER_STATUSES,
  PAYMENT_METHODS,
  THEMES,
} from "./enums";
import { ISO_DATE_REGEX } from "./dates";

/* ---------------------------------------------------------------------------
 * Field builders
 * ------------------------------------------------------------------------- */

export const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .email("Enter a valid email address");

/** Strong password for account setup. */
export const passwordField = z
  .string()
  .min(10, "Use at least 10 characters")
  .max(128)
  .regex(/[A-Za-z]/, "Include at least one letter")
  .regex(/[0-9]/, "Include at least one digit");

/**
 * bcrypt output ("$2a$10$…"), produced client-side in relief mode.
 * Shape-checked only; the server never recomputes it.
 */
export const passwordProofField = z
  .string()
  .regex(
    /^\$2[aby]\$\d{2}\$[./A-Za-z0-9]{53}$/,
    "Malformed password proof",
  );

const isoDateField = z
  .string()
  .regex(ISO_DATE_REGEX, "Expected a date as YYYY-MM-DD");

const optionalText = (max: number) =>
  z.string().trim().max(max).optional();

/**
 * Optional field for full-replace payloads where "" clears the stored value
 * (the route maps ""/undefined to NULL). Unlike optionalText fields, sending
 * the key with an empty string is explicitly allowed.
 */
const clearableTextField = (max: number) =>
  z.string().trim().max(max).optional();

const upiIdField = z
  .string()
  .trim()
  .max(100)
  .refine((v) => v === "" || /^[\w.-]{2,64}@[A-Za-z]{2,64}$/.test(v), {
    message: "Expected UPI ID format name@bank",
  })
  .optional();

const twoDecimalPlaces = (v: number) =>
  Math.abs(v * 100 - Math.round(v * 100)) < 1e-6;

const threeDecimalPlaces = (v: number) =>
  Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6;

/* ---------------------------------------------------------------------------
 * Auth
 * ------------------------------------------------------------------------- */

const passwordOrProof = <T extends z.ZodRawShape>(shape: T) =>
  z.object(shape).superRefine((value, ctx) => {
    const hasPassword = "password" in value && value.password !== undefined;
    const hasProof =
      "passwordProof" in value && value.passwordProof !== undefined;
    if (!hasPassword && !hasProof) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["password"],
        message: "Password is required",
      });
    }
  });

export const loginSchema = passwordOrProof({
  email: emailField,
  password: z.string().min(1).max(128).optional(),
  passwordProof: passwordProofField.optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const setupSchema = passwordOrProof({
  token: z.string().min(32).max(128),
  password: passwordField.optional(),
  passwordProof: passwordProofField.optional(),
});
export type SetupInput = z.infer<typeof setupSchema>;

/* ---------------------------------------------------------------------------
 * Clients
 * ------------------------------------------------------------------------- */

export const clientCreateSchema = z.object({
  name: z.string().trim().min(2, "Name is too short").max(200),
  email: emailField,
  phone: optionalText(20),
  address: optionalText(500),
  city: optionalText(100),
  state: optionalText(100),
  pincode: optionalText(20),
  notes: optionalText(5000),
});
export type ClientCreateInput = z.infer<typeof clientCreateSchema>;

/**
 * Email is deliberately immutable: in relief mode the bcrypt salt is derived
 * from the email, so changing it would invalidate the client's password.
 */
export const clientUpdateSchema = clientCreateSchema
  .omit({ email: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type ClientUpdateInput = z.infer<typeof clientUpdateSchema>;

/* ---------------------------------------------------------------------------
 * Matters
 * ------------------------------------------------------------------------- */

export const matterCreateSchema = z.object({
  clientId: z.string().uuid("Select a client"),
  title: z.string().trim().min(2, "Title is too short").max(200),
  matterDescription: optionalText(5000),
  caseType: z.string().trim().min(2, "Case type is required").max(100),
  caseNumber: optionalText(100),
  court: optionalText(200),
  nextHearingDate: isoDateField.nullable().optional(),
  status: z.enum(MATTER_STATUSES).default("active"),
});
export type MatterCreateInput = z.infer<typeof matterCreateSchema>;

export const matterUpdateSchema = matterCreateSchema
  .omit({ clientId: true })
  .partial();
export type MatterUpdateInput = z.infer<typeof matterUpdateSchema>;

/* ---------------------------------------------------------------------------
 * Invoices & payments
 * ------------------------------------------------------------------------- */

export const invoiceItemSchema = z.object({
  description: z.string().trim().min(1, "Description is required").max(500),
  quantity: z
    .number()
    .positive()
    .refine(threeDecimalPlaces, "Maximum 3 decimal places")
    .default(1),
  unitAmount: z
    .number()
    .min(0)
    .refine(twoDecimalPlaces, "Maximum 2 decimal places"),
});
export type InvoiceItemInput = z.infer<typeof invoiceItemSchema>;

export const invoiceCreateSchema = z
  .object({
    matterId: z.string().uuid("Select a matter"),
    status: z.enum(["draft", "pending"]).default("pending"),
    dueDate: isoDateField.optional(),
    notes: optionalText(2000),
    items: z.array(invoiceItemSchema).min(1).optional(),
    amount: z
      .number()
      .positive()
      .refine(twoDecimalPlaces, "Maximum 2 decimal places")
      .optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.items?.length && value.amount === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["items"],
        message: "Provide at least one line item or a total amount",
      });
    }
  });
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;

export const invoiceUpdateSchema = z.object({
  status: z.enum(INVOICE_STATUSES).optional(),
  dueDate: isoDateField.nullable().optional(),
  notes: optionalText(2000),
  items: z.array(invoiceItemSchema).min(1).optional(),
});
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;

export const paymentCreateSchema = z.object({
  amount: z
    .number()
    .positive()
    .refine(twoDecimalPlaces, "Maximum 2 decimal places"),
  method: z.enum(PAYMENT_METHODS).default("upi"),
  date: isoDateField.optional(),
  referenceId: optionalText(100),
});
export type PaymentCreateInput = z.infer<typeof paymentCreateSchema>;

/* ---------------------------------------------------------------------------
 * Firm settings
 * ------------------------------------------------------------------------- */

export const firmSettingsSchema = z.object({
  firmName: z.string().trim().min(2, "Firm name is too short").max(200),
  address: clearableTextField(500),
  gstin: z
    .string()
    .trim()
    .max(15)
    .refine((v) => v === "" || /^[0-9A-Z]{0,15}$/.test(v), {
      message: "GSTIN uses uppercase letters and digits only",
    })
    .optional(),
  upiId: upiIdField,
  invoicePrefix: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9\-_]+$/, "Letters, digits, hyphen and underscore only"),
  theme: z.enum(THEMES),
});
export type FirmSettingsInput = z.infer<typeof firmSettingsSchema>;

/* ---------------------------------------------------------------------------
 * List/query params
 * ------------------------------------------------------------------------- */

export const clientsQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
});

export const mattersQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(MATTER_STATUSES).optional(),
  clientId: z.string().uuid().optional(),
  sort: z.enum(["hearing", "updated"]).optional(),
});

export const invoicesQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(INVOICE_STATUSES).optional(),
  clientId: z.string().uuid().optional(),
});

export const brandingSchema = z.enum(AUTH_MODES);
